import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { DashboardEscalations } from "@/lovable-demo/components/dashboard-escalations";
import { KpiCard, Panel, RiskBadge, StatusChip } from "@/lovable-demo/components/bits";
import {
  C1000Chart,
  CostForecastChart,
  ImrChart,
  Sparkline,
} from "@/lovable-demo/components/charts";
import {
  blendedForecast,
  blendedSeries,
  portfolio,
  recordsForProgram,
  topFive,
} from "@/lovable-demo/lib/warranty/aggregate";
import { fmtCompactCurrency, fmtCurrency, fmtNumber } from "@/lovable-demo/lib/warranty/calc";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";

export const Route = createFileRoute("/demo/dashboard")({
  head: () => ({
    meta: [
      { title: "Executive Warranty Dashboard — Broadview" },
      {
        name: "description",
        content:
          "Warranty KPIs, Top 5 problems, financial exposure, C/1000 trend, I-MR control chart and projected cost in one executive view.",
      },
      { property: "og:title", content: "Executive Warranty Dashboard — Broadview" },
      {
        property: "og:description",
        content:
          "Warranty KPIs, Top 5 problems, financial exposure, C/1000 trend, I-MR control chart and projected cost in one executive view.",
      },
    ],
  }),
  component: Dashboard,
});

function Dashboard() {
  const { data, activeProgramId } = useWarranty();
  const records = recordsForProgram(data, activeProgramId);
  const program = data.programs.find((p) => p.id === activeProgramId);
  const stats = portfolio(records);
  const five = topFive(records);
  const c1000 = program ? program.trend : blendedSeries(records, "c1000");
  const imr = blendedSeries(records, "imr");
  const forecast = blendedForecast(records);
  const forecastEnd = forecast.at(-1)?.aiForecast ?? 0;

  return (
    <AppShell
      title="Executive Warranty Dashboard"
      subtitle={
        program
          ? `${program.code} · ${program.name} · MY${program.modelYear}`
          : "All programs · consolidated portfolio"
      }
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Claims per 1,000"
          value={(program?.c1000 ?? stats.c1000).toFixed(1)}
          delta={program ? `target ${program.c1000Target.toFixed(1)}` : undefined}
          hint="12-month rolling"
          tone={program && program.c1000 > program.c1000Target ? "bad" : "good"}
          chart={
            <Sparkline
              data={c1000}
              tone={program && program.c1000 > program.c1000Target ? "2" : "4"}
            />
          }
        />
        <KpiCard
          label="Open Problem Records"
          value={String(stats.open)}
          delta={`${stats.critical} critical`}
          hint={`${stats.closed} closed · ${stats.gated} awaiting evidence`}
          tone="warn"
        />
        <KpiCard
          label="Financial exposure"
          value={fmtCompactCurrency(stats.exposure)}
          delta={`${fmtCompactCurrency(stats.actual)} incurred`}
          hint="Actual + projected warranty cost"
          tone="bad"
        />
        <KpiCard
          label="Remaining exposure"
          value={fmtCompactCurrency(stats.npr)}
          delta={`${fmtCompactCurrency(stats.delay)}/mo delay cost`}
          hint="Total exposure minus actual cost to date"
          tone="good"
        />
      </div>

      <div className="mt-4">
        <DashboardEscalations records={records} />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Panel
          title="Management Top 5"
          subtitle="Risk-ranked and linked to the governed record"
          className="xl:col-span-2"
          actions={
            <Link to="/demo/top5" className="text-xs text-primary hover:underline">
              Top 5 intelligence
            </Link>
          }
        >
          <ul className="divide-y divide-border">
            {five.map((r, i) => (
              <li key={r.id}>
                <Link
                  to="/demo/problem-record/$id"
                  params={{ id: r.id }}
                  className="flex flex-wrap items-center gap-3 py-3 transition-colors hover:text-primary"
                >
                  <span className="num w-6 text-lg font-semibold text-muted-foreground">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{r.title}</span>
                    <span className="num block text-xs text-muted-foreground">
                      {r.code} · {r.component} · {r.supplier}
                    </span>
                  </span>
                  <span className="h-8 w-20 shrink-0">
                    <Sparkline data={r.c1000} tone="2" />
                  </span>
                  <span className="num w-24 text-right text-sm">
                    {fmtCompactCurrency(r.financial.exposure)}
                  </span>
                  <RiskBadge risk={r.risk} score={r.riskScore} />
                  <StatusChip status={r.status} />
                </Link>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Financial exposure" subtitle="Portfolio position">
          <dl className="space-y-3 text-sm">
            {[
              ["Actual cost to date", fmtCurrency(stats.actual)],
              ["Total exposure", fmtCurrency(stats.exposure)],
              ["Remaining exposure", fmtCurrency(stats.npr)],
              ["Delay cost per month", fmtCurrency(stats.delay)],
              ["Supplier recovery target", fmtCurrency(stats.recoveryTarget)],
              ["Recovered to date", fmtCurrency(stats.recovered)],
            ].map(([k, v]) => (
              <div
                key={k}
                className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-2"
              >
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="num font-semibold">{v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-4 rounded border border-border bg-elevated/50 p-3 text-xs text-muted-foreground">
            The formula-based projection reaches{" "}
            <span className="num font-semibold text-warning">
              {fmtCompactCurrency(forecastEnd)}
            </span>{" "}
            of cumulative cost. It applies month-over-month growth from the initial record values
            and is not an AI or statistical model.
          </div>
          <Link
            to="/demo/financial"
            className="mt-3 inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Financial intelligence <ArrowUpRight className="size-3.5" />
          </Link>
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <Panel title="C/1000 trend" subtitle="Claims per 1,000 units in service">
          <C1000Chart data={c1000} target={program?.c1000Target} />
        </Panel>
        <Panel title="I-MR control chart" subtitle="Individual and moving range with 3σ limits">
          <ImrChart data={imr} />
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Panel
          title="Cost vs projected cost"
          subtitle="Month-over-month growth projection; not an AI or statistical model"
          className="xl:col-span-2"
        >
          <CostForecastChart data={forecast} />
        </Panel>
        <Panel title="Program signals" subtitle="Claims per 1,000 by program">
          <ul className="space-y-3">
            {data.programs.map((p) => (
              <li key={p.id} className="flex items-center gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">{p.name}</span>
                  <span className="num text-xs text-muted-foreground">
                    {fmtNumber(p.unitsInService)} units · target {p.c1000Target.toFixed(1)}
                  </span>
                </span>
                <span className="h-8 w-24 shrink-0">
                  <Sparkline data={p.trend} tone={p.c1000 > p.c1000Target ? "2" : "4"} />
                </span>
                <span
                  className={`num w-12 text-right text-sm font-semibold ${
                    p.c1000 > p.c1000Target ? "text-warning" : "text-success"
                  }`}
                >
                  {p.c1000.toFixed(1)}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </AppShell>
  );
}
